import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { History, Loader2, RefreshCw, Search, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUDIT_ADMIN_EMAIL = 'f.conti@crimpiantielettrici.it';
const PAGE_SIZE = 100;

const MODULI = [
  { value: 'documenti', label: 'Documenti' },
  { value: 'documenti_cantiere', label: 'Documenti Cantiere' },
  { value: 'archivio', label: 'Archivio' },
  { value: 'ddt', label: 'DDT' },
  { value: 'rapportini', label: 'Rapportini' },
  { value: 'fatture_emesse', label: 'Fatture Emesse' },
  { value: 'fatture_ricevute', label: 'Fatture Ricevute' },
  { value: 'contabilita_cantieri', label: 'Contabilità Cantieri' },
  { value: 'cantieri', label: 'Cantieri' },
  { value: 'scansioni', label: 'Scansioni' },
  { value: 'modelli_documenti', label: 'Modelli Documenti' },
  { value: 'fatture', label: 'Fatture (upload XML)' },
  { value: 'upload_file', label: 'Upload File' },
];
const MODULO_LABEL = Object.fromEntries(MODULI.map(m => [m.value, m.label]));

const AZIONE_COLORS = {
  creazione: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  modifica: 'bg-blue-100 text-blue-700 border-blue-200',
  eliminazione: 'bg-red-100 text-red-700 border-red-200',
  'eliminazione multipla': 'bg-red-100 text-red-700 border-red-200',
  'eliminazione pagamento': 'bg-red-100 text-red-700 border-red-200',
  'cambio stato': 'bg-amber-100 text-amber-700 border-amber-200',
  'registrazione pagamento': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'modifica pagamento': 'bg-blue-100 text-blue-700 border-blue-200',
  importazione: 'bg-purple-100 text-purple-700 border-purple-200',
  'caricamento file': 'bg-purple-100 text-purple-700 border-purple-200',
  'invio a operai': 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export default function CronologiaPage() {
  const { user } = useAuth();
  const isAuthorized = (user?.email || '').trim().toLowerCase() === AUDIT_ADMIN_EMAIL;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modulo, setModulo] = useState('all');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const fetchLogs = useCallback(async (skip = 0, append = false) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (modulo !== 'all') params.modulo = modulo;
      if (appliedSearch) params.q = appliedSearch;
      const r = await axios.get(`${API}/audit-log/files`, { params, withCredentials: true });
      setItems(prev => append ? [...prev, ...(r.data.items || [])] : (r.data.items || []));
      setTotal(r.data.total ?? (r.data.items || []).length);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento cronologia');
    } finally {
      setLoading(false);
    }
  }, [modulo, appliedSearch]);

  useEffect(() => {
    if (isAuthorized) fetchLogs(0);
  }, [isAuthorized, fetchLogs]);

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6" data-testid="cronologia-denied">
        <ShieldAlert className="h-16 w-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Accesso negato</h1>
        <p className="text-sm text-slate-600 max-w-md">La cronologia modifiche è riservata all'amministratore autorizzato.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cronologia-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <History className="h-7 w-7" />Cronologia Modifiche
        </h1>
        <p className="text-slate-500 mt-1">Storico immutabile di tutte le operazioni su documenti, DDT, rapportini, fatture, incassi e contabilità cantieri</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="space-y-1 min-w-[200px]">
            <Label className="text-xs text-slate-500">Sezione</Label>
            <Select value={modulo} onValueChange={setModulo}>
              <SelectTrigger data-testid="cronologia-filter-modulo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le sezioni</SelectItem>
                {MODULI.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[220px]">
            <Label className="text-xs text-slate-500">Cerca (elemento, utente, note...)</Label>
            <div className="flex gap-2">
              <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') setAppliedSearch(search); }} placeholder="es. Fattura 12, Mario..." data-testid="cronologia-search-input" />
              <Button variant="outline" onClick={() => setAppliedSearch(search)} data-testid="cronologia-search-btn"><Search className="h-4 w-4" /></Button>
            </div>
          </div>
          <Button variant="outline" onClick={() => fetchLogs(0)} disabled={loading} data-testid="cronologia-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Aggiorna
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500" data-testid="cronologia-empty">Nessuna operazione registrata con questi filtri.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="cronologia-table">
                <TableHeader>
                  <TableRow className="bg-slate-50 border-slate-200">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">Data e ora</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Utente</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Sezione</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Azione</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Elemento</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Ver.</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(log => (
                    <TableRow key={log.id} className="border-slate-100">
                      <TableCell className="text-sm whitespace-nowrap font-mono text-slate-700">{fmtDate(log.timestamp)}</TableCell>
                      <TableCell className="text-sm font-medium text-slate-900">{log.user_name || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500">{log.user_email || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{MODULO_LABEL[log.modulo] || log.modulo}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className={`text-xs ${AZIONE_COLORS[log.azione] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{log.azione}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-700 max-w-[260px] truncate" title={log.file_nome}>{log.file_nome || <span className="text-slate-400 italic">-</span>}</TableCell>
                      <TableCell className="text-center text-sm text-slate-500">{log.versione || 1}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[220px] truncate" title={log.note}>{log.note || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {items.length > 0 && items.length < total && (
            <div className="p-3 text-center border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={() => fetchLogs(items.length, true)} disabled={loading} data-testid="cronologia-load-more">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Carica altri ({items.length} di {total})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
